import { NUMBER_MOTION_SELECTOR, EXCLUDED_NUMBER_SELECTOR } from './data-motion-selectors.js';

// Data presentation is independent of the 220ms page / 200ms tab / 400ms theme.
export const DATA_MOTION = { numberDuration:640, chartDuration:680, easing:'cubic-bezier(.22,1,.36,1)' };
const numberFormats = new Map();

export function parseMotionNumber(text) {
  if (typeof text !== 'string' || !text.trim()) return null;
  const match = text.trim().match(/^([+\-−]?)(\d(?:[\d \u00a0\u202f]*\d)?)(?:([,.])(\d{1,3}))?(\s*(?:₽|%|°\s*[CС]?|шт\.?|кг|г|л|ч|мин|д)?)$/u);
  if (!match) return null;
  const value = Number(`${match[1] === '−' ? '-' : match[1]}${match[2].replace(/\s/g, '')}${match[4] ? `.${match[4]}` : ''}`);
  if (!Number.isFinite(value)) return null;
  return { value, precision:match[4]?.length || 0, prefix:match[1], suffix:match[5], exact:text };
}

export function formatMotionNumber(parsed, progress) {
  if (!parsed) return '';
  if (progress >= 1) return parsed.exact;
  const magnitude = Math.abs(parsed.value) * Math.max(0, Math.min(1, Number(progress) || 0));
  if (!numberFormats.has(parsed.precision)) numberFormats.set(parsed.precision, new Intl.NumberFormat('ru-RU', { minimumFractionDigits:parsed.precision, maximumFractionDigits:parsed.precision }));
  const number = numberFormats.get(parsed.precision).format(magnitude);
  return `${parsed.prefix}${number}${parsed.suffix}`;
}

const roots = new WeakMap(), plotControllers = new WeakMap(), liveRoots = new Set();
const frameJobs = new Set();
let frame;
const ease = value => 1 - (1 - value) ** 3;
function schedule(job) {
  frameJobs.add(job);
  if (frame === undefined) frame = requestAnimationFrame(tick);
}
function tick(now) {
  frame = undefined;
  for (const job of [...frameJobs]) if (!job.step(now)) frameJobs.delete(job);
  if (frameJobs.size) frame = requestAnimationFrame(tick);
}
function unschedule(job) {
  frameJobs.delete(job);
  if (!frameJobs.size && frame !== undefined) { cancelAnimationFrame(frame); frame = undefined; }
}
function ownerFor(element) {
  return element.closest?.('#main-content') || element.closest?.('.dp-main') || element.closest?.('[data-unified-surface]') || element;
}
function sourceText(element) {
  const exact = element.matches('.dashboard-number') && element.querySelector('.dashboard-number-exact');
  if (exact) return exact.textContent;
  return [...element.childNodes].filter(node => !node.matches?.('[data-number-motion-overlay]')).map(node => node.textContent).join('');
}
function numericKey(element, owner) {
  const path = [];
  for (let node = element; node && node !== owner; node = node.parentElement) {
    const classes = [...node.classList].filter(name => !/^(?:active|selected|is-|dp-positive|dp-negative|turned|motion-)/.test(name)).sort().join('.');
    const siblings = node.parentElement ? [...node.parentElement.children].filter(item => item.tagName === node.tagName && !item.matches('[data-number-motion-overlay]')) : [];
    path.unshift(`${node.tagName}.${classes}:${node.getAttribute('data-block') || ''}:${siblings.indexOf(node)}`);
  }
  return path.join('/');
}
function visible(element) {
  const rect = element.getBoundingClientRect();
  const win = element.ownerDocument.defaultView;
  return rect.width > 0 && rect.height > 0 && rect.bottom >= 0 && rect.right >= 0 && rect.top <= win.innerHeight && rect.left <= win.innerWidth
    && element.checkVisibility?.({ checkVisibilityCSS:true }) !== false;
}
function held(root) { return root.doc.documentElement.hasAttribute('data-workspace-transition'); }
function disabled(root) { return root.media.matches || root.printMedia.matches || root.printing || root.doc.hidden; }
function priority(element) { return element.matches('.dashboard-number') ? 0 : element.matches('.revenue-money,.checks-money,.dishes-money,.temperature-number') ? 1 : 2; }

function ensureRoot(owner) {
  if (!owner?.querySelectorAll || !owner.ownerDocument?.defaultView) return null;
  if (roots.has(owner)) return roots.get(owner);
  const doc = owner.ownerDocument, win = doc.defaultView;
  const media = win.matchMedia('(prefers-reduced-motion: reduce)');
  const printMedia = win.matchMedia('print');
  const root = { owner, doc, win, media, printMedia, printing:false, seenNumbers:new Set(), seenCharts:new Set(), numbers:new Map(), charts:new Set(), pending:new Set(), replayTargets:new Set(), queued:false, destroyed:false };
  roots.set(owner, root); liveRoots.add(root);
  owner.setAttribute('data-data-motion-surface', '');
  const finishAll = () => {
    for (const record of root.numbers.values()) record.finish();
    for (const chart of root.charts) chart.finish();
  };
  const preference = () => { if (media.matches) finishAll(); };
  const visibility = () => { if (doc.hidden) finishAll(); };
  const beforePrint = () => { root.printing = true; finishAll(); };
  const afterPrint = () => { root.printing = false; };
  const printChanged = () => { if (printMedia.matches) finishAll(); };
  media.addEventListener('change', preference);
  doc.addEventListener('visibilitychange', visibility);
  printMedia.addEventListener('change', printChanged);
  win.addEventListener('beforeprint', beforePrint);
  win.addEventListener('afterprint', afterPrint);
  win.addEventListener('nur:theme-change', finishAll);
  root.intersections = win.IntersectionObserver && new win.IntersectionObserver(entries => {
    for (const entry of entries) if (entry.isIntersecting) {
      root.intersections.unobserve(entry.target);
      const record = root.numbers.get(entry.target) || plotControllers.get(entry.target);
      record?.enter();
    }
  });
  const flush = () => {
    root.queued = false;
    if (root.destroyed) return;
    for (const record of [...root.numbers.values()]) {
      if (!record.element.isConnected) { record.finish(); root.intersections?.unobserve(record.element); root.numbers.delete(record.element); }
      else record.sync();
    }
    for (const target of root.pending) if (target.isConnected) scanNumbers(root, target);
    root.pending.clear();
  };
  root.scan = target => {
    root.pending.add(target);
    if (!root.queued) { root.queued = true; queueMicrotask(flush); }
  };
  root.observer = new win.MutationObserver(records => {
    for (const record of records) {
      const target = record.target.nodeType === 1 ? record.target : record.target.parentElement;
      if (!target || target.closest('[data-number-motion-overlay]')) continue;
      const additions = [...record.addedNodes].filter(node => node.nodeType === 1 && !node.matches('[data-number-motion-overlay]'));
      if (additions.length) for (const node of additions) root.scan(node);
      else if (record.type === 'characterData' || ![...record.addedNodes, ...record.removedNodes].every(node => node.matches?.('[data-number-motion-overlay]'))) root.scan(target);
    }
  });
  // Observe this content owner only, not document, attributes, keystrokes or
  // animation frames. Sources remain Vue-owned; only visual copies change.
  root.observer.observe(owner, { childList:true, subtree:true, characterData:true });
  root.destroy = () => {
    root.destroyed = true; finishAll(); root.observer.disconnect(); root.intersections?.disconnect();
    media.removeEventListener('change', preference); doc.removeEventListener('visibilitychange', visibility);
    printMedia.removeEventListener('change', printChanged);
    win.removeEventListener('beforeprint', beforePrint); win.removeEventListener('afterprint', afterPrint); win.removeEventListener('nur:theme-change', finishAll);
    owner.removeAttribute('data-data-motion-surface'); roots.delete(owner); liveRoots.delete(root);
    root.numbers.clear(); root.charts.clear(); root.pending.clear();
  };
  root.scan(owner);
  return root;
}

function scanNumbers(root, target) {
  const found = [...(target.matches?.(NUMBER_MOTION_SELECTOR) ? [target] : []), ...target.querySelectorAll(NUMBER_MOTION_SELECTOR)];
  const ready = [];
  for (const element of found.sort((a, b) => priority(a) - priority(b))) {
    if (!root.owner.contains(element) || element.closest(EXCLUDED_NUMBER_SELECTOR) || root.numbers.has(element)) continue;
    if (element.querySelector('[data-number-final],[data-number-animate="false"],input,textarea,select,[contenteditable]:not([contenteditable="false"])')) continue;
    let covered = false;
    for (let parent = element.parentElement; parent && root.owner.contains(parent); parent = parent.parentElement) {
      if (root.numbers.has(parent)) { covered = true; break; }
    }
    if (covered) continue;
    const parsed = parseMotionNumber(sourceText(element));
    if (!parsed) continue;
    const record = createNumberRecord(root, element, parsed);
    root.numbers.set(element, record);
    ready.push({ record, element });
  }
  // Read the visible batch before adding any visual copies, avoiding one
  // layout flush per table value. Offscreen values wait for first exposure.
  for (const item of ready) item.visible = visible(item.element);
  for (const { record, element, visible:onscreen } of ready) {
    if (disabled(root)) record.finish();
    else if (onscreen || !root.intersections) record.enter(false, true);
    else root.intersections.observe(element);
  }
}

function createNumberRecord(root, element, initial) {
  const key = numericKey(element, root.owner);
  const visual = element.matches('.dashboard-number') ? element.querySelector('.dashboard-number-visual') : null;
  let parsed = initial, overlay, start, active = false;
  element.setAttribute('data-number-final', parsed.exact);
  element.setAttribute('data-number-display', parsed.exact);
  if (!element.hasAttribute('data-number-motion-count')) element.setAttribute('data-number-motion-count', '0');
  const write = text => {
    element.setAttribute('data-number-display', text);
    if (visual) visual.setAttribute('data-display', text);
    else if (overlay?.isConnected) overlay.setAttribute('data-number-motion-overlay-value', text);
  };
  const record = { element,
    sync() {
      const text = sourceText(element);
      if (text !== parsed.exact) {
        parsed = parseMotionNumber(text) || { ...parsed, exact:text };
        element.setAttribute('data-number-final', text);
        record.finish();
      }
    },
    finish() {
      unschedule(record); active = false; start = undefined;
      const exact = sourceText(element);
      write(exact);
      element.setAttribute('data-number-final', exact);
      element.removeAttribute('data-number-animating');
      overlay?.remove(); overlay = undefined;
    },
    enter(force = false, alreadyVisible = false) {
      if (active || !element.isConnected || root.destroyed) return;
      if (disabled(root) || !alreadyVisible && !visible(element) || !force && root.seenNumbers.has(key)) { record.finish(); return; }
      root.seenNumbers.add(key);
      parsed = parseMotionNumber(sourceText(element));
      if (!parsed) return;
      if (!visual) {
        overlay = root.doc.createElement('span');
        overlay.setAttribute('data-number-motion-overlay', ''); overlay.setAttribute('aria-hidden', 'true');
        element.append(overlay);
      }
      element.setAttribute('data-number-final', parsed.exact);
      element.setAttribute('data-number-animating', '');
      element.setAttribute('data-number-motion-count', String(Number(element.getAttribute('data-number-motion-count')) + 1));
      write(formatMotionNumber(parsed, 0)); active = true; start = undefined;
      schedule(record);
    },
    step(now) {
      if (!active) return false;
      if (!element.isConnected || root.destroyed || disabled(root)) { record.finish(); return false; }
      if (held(root)) return true;
      if (start === undefined) {
        start = now;
      }
      const progress = Math.min(1, (now - start) / DATA_MOTION.numberDuration);
      if (progress >= 1) { record.finish(); return false; }
      write(formatMotionNumber(parsed, ease(progress)));
      return true;
    }
  };
  return record;
}

export function mountDataSurface(element) {
  queueMicrotask(() => { if (element.isConnected) ensureRoot(ownerFor(element))?.scan(element); });
}
export function replayDataSurface(element) {
  const root = ensureRoot(ownerFor(element));
  if (!root) return;
  root.replayTargets.add(element);
  if (root.replayQueued) return;
  root.replayQueued = true;
  queueMicrotask(() => {
    root.replayQueued = false;
    if (root.destroyed) return;
    const targets = [...root.replayTargets].filter(target => target.isConnected && ![...root.replayTargets].some(parent => parent !== target && parent.contains(target)));
    root.replayTargets.clear();
    for (const target of targets) {
      const prefix = numericKey(target, root.owner);
      for (const seen of [root.seenNumbers, root.seenCharts]) for (const key of seen) if (!prefix || key === prefix || key.startsWith(`${prefix}/`) || key.startsWith(`${prefix}:`)) seen.delete(key);
      root.scan(target);
    }
    queueMicrotask(() => {
      if (root.destroyed) return;
      for (const target of targets) {
        for (const record of root.numbers.values()) if (target.contains(record.element)) { record.finish(); record.enter(true); }
        for (const chart of root.charts) if (target.contains(chart.element)) chart.replay();
      }
    });
  });
}
export function unmountDataSurface(element) { if (roots.has(element)) roots.get(element).destroy(); }

const PART_SELECTOR = '[data-chart-segment],[data-chart-line],[data-chart-area],[data-chart-point],[data-chart-bar],[data-chart-pointer]';
function geometry(element) {
  return ['d','points','stroke-dasharray','stroke-dashoffset','x','y','width','height','cx','cy','r'].map(name => element.getAttribute(name)).join('|')
    + `|${element.style.width}|${element.style.height}|${element.closest('svg')?.style.getPropertyValue('--gauge-fill') || ''}`;
}
function chartFrames(element, root) {
  const style = root.win.getComputedStyle(element);
  if (element.hasAttribute('data-chart-segment')) {
    const dash = element.getAttribute('stroke-dasharray') || style.strokeDasharray;
    if (dash && dash !== 'none') {
      // An explicit zero is a real zero, never a fallback full circle.
      if (!(Number.parseFloat(dash) > 0)) return null;
      return [{ strokeDasharray:'0 100' }, { strokeDasharray:dash }];
    }
    // A one-category full ring may not declare a dash array at all.
    const length = Number(element.getAttribute('pathLength')) || element.getTotalLength?.();
    if (length > 0) return [{ strokeDasharray:`0 ${length}` }, { strokeDasharray:`${length} ${length}` }];
  }
  if (element.hasAttribute('data-chart-line')) {
    const length = element.getTotalLength?.();
    if (length > 0 && (!style.strokeDasharray || style.strokeDasharray === 'none')) return [
      { strokeDasharray:`${length} ${length}`, strokeDashoffset:length },
      { strokeDasharray:`${length} ${length}`, strokeDashoffset:0 }
    ];
  }
  if (element.hasAttribute('data-chart-bar')) {
    const vertical = element.getAttribute('data-chart-axis') === 'y';
    const negative = element.getAttribute('data-chart-negative') === 'true';
    return [{ clipPath:vertical ? negative ? 'inset(0 0 100% 0)' : 'inset(100% 0 0 0)' : negative ? 'inset(0 0 0 100%)' : 'inset(0 100% 0 0)' }, { clipPath:'inset(0 0 0 0)' }];
  }
  if (element.hasAttribute('data-chart-pointer')) return [{ transform:'rotate(-90deg)' }, { transform:style.transform }];
  return [{ opacity:0 }, { opacity:style.opacity }];
}

function createChartController(element) {
  const root = ensureRoot(ownerFor(element));
  if (!root) return null;
  const records = new Map();
  let entered = false, destroyed = false;
  const controller = { element,
    scan() {
      if (destroyed) return;
      for (const [part, record] of records) {
        if (!element.contains(part)) { record.finish(); records.delete(part); }
        else if (record.signature !== geometry(part)) { record.finish(); record.signature = geometry(part); }
      }
      const parts = [...(element.matches(PART_SELECTOR) ? [element] : []), ...element.querySelectorAll(PART_SELECTOR)].filter(part => !part.closest('[data-chart-static]'));
      for (const [index, part] of parts.entries()) if (!records.has(part)) {
        const key = `${numericKey(element, root.owner)}:${part.tagName}:${index}`;
        let animation, started = false, active = false;
        const record = { signature:geometry(part),
          finish() {
            unschedule(record); active = false; animation?.cancel(); animation = undefined;
            if (![...records.values()].some(item => item.active)) element.removeAttribute('data-chart-animating');
          },
          get active() { return active; },
          enter(force = false) {
            if (active || disabled(root) || !visible(element) || !part.isConnected || !force && root.seenCharts.has(key)) return;
            root.seenCharts.add(key);
            try {
              if (!part.animate) return;
              const frames = chartFrames(part, root);
              if (!frames) return;
              animation = part.animate(frames, { duration:DATA_MOTION.chartDuration, easing:DATA_MOTION.easing, fill:'both' });
              animation.id = 'nur-chart-enter'; animation.pause(); animation.currentTime = 0;
              started = false; active = true; element.setAttribute('data-chart-animating', ''); schedule(record);
              animation.onfinish = () => record.finish();
            } catch { record.finish(); }
          },
          step() {
            if (!active) return false;
            if (!part.isConnected || root.destroyed || disabled(root)) { record.finish(); return false; }
            if (held(root)) return true;
            if (!started) { started = true; animation.play(); }
            return false;
          }
        };
        records.set(part, record);
        if (entered) record.enter();
      }
    },
    enter() { entered = true; controller.scan(); for (const record of records.values()) record.enter(); },
    replay() { controller.finish(); for (const record of records.values()) record.enter(true); },
    finish() { for (const record of records.values()) record.finish(); element.removeAttribute('data-chart-animating'); },
    destroy() {
      destroyed = true; controller.finish(); root.charts.delete(controller); root.intersections?.unobserve(element); records.clear(); plotControllers.delete(element);
      if (root.owner === element || !root.owner.isConnected) root.destroy();
    }
  };
  root.charts.add(controller); plotControllers.set(element, controller);
  controller.scan();
  if (visible(element) || !root.intersections) controller.enter();
  else root.intersections.observe(element);
  return controller;
}

export const chartMotion = {
  mounted(element) { queueMicrotask(() => { if (element.isConnected) createChartController(element); }); },
  updated(element) { plotControllers.get(element)?.scan(); },
  unmounted(element) { plotControllers.get(element)?.destroy(); }
};
